/****************************************************************************
 **
 ** Copyright (C) 2021 The Qt Company Ltd.
 ** Contact: https://www.qt.io/licensing/
 **
 ** This file is part of Qt Bridge Plugin.
 **
 ** Commercial License Usage
 ** Licensees holding valid commercial Qt licenses may use this file in
 ** accordance with the commercial license agreement provided with the
 ** Software or, alternatively, in accordance with the terms contained in
 ** a written agreement between you and The Qt Company. For licensing terms
 ** and conditions see https://www.qt.io/terms-conditions. For further
 ** information use the contact form at https://www.qt.io/contact-us.
 **
 ****************************************************************************/

import $ from "jquery";
import * as Constants from "../../src/constants.js";
import {
    SketchLayerType as SLT,
    ExportType as ET,
    ConstExportOptions as CEO,
    ValidExportFormats as VEF,
    ValidExportScale as VES
} from "../../src/constants.js";

let Controls = {
    None: 0,
    QmlId: 1,
    ExportAs: 2,
    QmlType: 4,
    Imports: 8,
    QmlProps: 16,
    Alias: 32,
    Clip: 64,
    Visible: 128,
    RenderText: 256,
    Annotation: 512
};
Controls.LayerMeta =
    Controls.QmlId |
    Controls.ExportAs |
    Controls.QmlType |
    Controls.Imports |
    Controls.QmlProps |
    Controls.Alias |
    Controls.Clip |
    Controls.Visible |
    Controls.RenderText |
    Controls.Annotation;

let IdDict = {
    QmlId: "#qml_id_edit",
    1: "#qml_id_edit",
    ExportAs: "#export_type_combo",
    2: "#export_type_combo",
    QmlType: "#qml_type_edit",
    4: "#qml_type_edit",
    Imports: "#import_statements_textarea",
    8: "#import_statements_textarea",
    QmlProps: "#qml_properties_textarea",
    16: "#qml_properties_textarea",
    Alias: "#alias_check",
    32: "#alias_check",
    Clip: "#clip_check",
    64: "#clip_check",
    Visible: "#visible_check",
    128: "#visible_check",
    RenderText: "#render_text_check",
    256: "#render_text_check",
    Annotation: "#annotation_textarea",
    512: "#annotation_textarea",
    QmlIdReset: "#qml_id_reset_btn",
    Home: "#home_btn",
    Settings: "#settings_btn",
    About: "#about_btn",
    License: "#license_btn",
    Version: "#version",
    Build: "#build",
    PanelSettings: "#settings_panel",
    PanelAbout: "#about_panel",
    Help: "#help_btn",
    DocName: "#document_edit",
    LayerName: "#layer_edit",
    SkippedWarning: "#skipped_warning",
    Export: "#export_btn",
    ProgressModal: "#progress_modal",
    ProgressLoading: "#progress_loading",
    ProgressLog: "#progress_textarea",
    ProgressBar: "#progress_bar",
    ProgressOK: "#progress_ok_btn",
    ShowExportDir: "#open_export_target_link",
    PathEdit: "#export_path_edit",
    PathBtn: "#export_path_btn",
    PathReset: "#export_path_reset_btn",
    PngMetadata: "#png_metadata",
    JpgProgressive: "#jpg_progressive",
    SvgNamespaces: "#svg_namespaces",
    SvgCompact: "#svg_compact",
    UiScaleEdit: "#ui_scale_edit",
    OrganizeInPages: "#organize_in_pages",
    FormatPanel: "#asset_format_container",
    FormatCombo: "#format_combo",
    FormatScale: "#scale_combo",
    FormatAdd: "#add_asset_btn",
    FormatDelete: "#delete_scale_format_btn"
};

(function () {
    "use strict";

    // Enable all UI
    disableControls(Controls.None);

    // Populate Export type combo
    let exportCombo = $(IdDict.ExportAs);
    for (let exportType in ET) {
        let str = ET[exportType];
        exportCombo.append(`<option value=${str}>${capitalize(str)}</option>`);
    }

    // Register HTML callbacks
    $(document).on("mouseenter", ".image-button", onImageButtonHoverIn);
    $(document).on("mouseleave", ".image-button", onImageButtonHoverOut);

    // Controls
    $(IdDict.Home).click(onHome);
    $(IdDict.Settings).click(onSettings);
    $(IdDict.About).click(onAbout);
    $(IdDict.License).click(onLicense);

    $(IdDict.Help).click(onHelp);

    $(IdDict.QmlId).change(onIdChanged);
    $(IdDict.QmlId).blur(onIdChanged);
    $(IdDict.QmlIdReset).click(onQmlIdReset);
    $(IdDict.ExportAs).change(onExportAsChanged);
    $(IdDict.RenderText).change(onRenderTextChanged);
    $(IdDict.QmlType).change(onQmlTypeChanged);
    $(IdDict.QmlType).blur(onQmlTypeChanged);
    $(IdDict.Imports).change(onImportStatementsChanged);
    $(IdDict.Imports).blur(onImportStatementsChanged);
    $(IdDict.QmlProps).change(onQmlPropertiesChanged);
    $(IdDict.QmlProps).blur(onQmlPropertiesChanged);
    $(IdDict.Alias).change(onAliasChanged);
    $(IdDict.Clip).change(onClipChanged);
    $(IdDict.Visible).change(onVisibleChanged);
    $(IdDict.Annotation).change(onAnnotationChanged)
    $(IdDict.Annotation).blur(onAnnotationChanged)

    $(IdDict.Export).click(onExport);
    $(IdDict.ProgressOK).click(onCloseExport);
    $(IdDict.ShowExportDir).click(onShowExportDir);
    $(IdDict.PngMetadata).change(onExportOptionChanged);
    $(IdDict.JpgProgressive).change(onExportOptionChanged);
    $(IdDict.SvgNamespaces).change(onExportOptionChanged);
    $(IdDict.SvgCompact).change(onExportOptionChanged);
    $(IdDict.UiScaleEdit).change(onUiScaleChanged);

    $(IdDict.OrganizeInPages).change(onOrganizeInPages)

    $(IdDict.PathBtn).click(onExportPath);
    $(IdDict.PathReset).click(onExportPathReset);

    $(document).on("change", IdDict.FormatCombo, onScaleFormatChanged);
    $(document).on("change", IdDict.FormatScale, onScaleFormatChanged);
    $(document).on("click", IdDict.FormatAdd, addScaleFormat);
    $(document).on("click", IdDict.FormatDelete, onDeleteScaleFormat);
    $(document).on("contextmenu", e => {
        e.preventDefault();
    });
})();

//*****************************************************************************
// UI Event Handlers.
//*****************************************************************************
function onImageButtonHoverIn() {
    let src = this.attributes.src.value;
    $(this).css("transform", "scale(1.2)");
    this.attributes.src.value = src.slice(0, src.lastIndexOf(".svg")) + "_active.svg";
}

function onImageButtonHoverOut() {
    let src = this.attributes.src.value;
    $(this).css("transform", "scale(1.0)");
    this.attributes.src.value = src.slice(0, src.lastIndexOf("_active.svg")) + ".svg";
}
function onIdChanged(e) {
    "use strict";
    let newValue = this.value;
    if (newValue === "") {
        return;
    }
    pluginApi("setLayerMetadata", { name: "qmlId", value: newValue });
}


function onQmlIdReset() {
    pluginApi("resetLayerMetadata", "qmlId");
}

function onExportAsChanged(e) {
    "use strict";
    let newExportType;
    let selectedOption = $(this).val();
    switch (selectedOption) {
        case "component":
            newExportType = ET.AsComponent;
            break;
        case "child":
            newExportType = ET.AsChild;
            break;
        case "merged":
            newExportType = ET.MergedToParent;
            break;
        case "skip":
            newExportType = ET.SkipExport;
            break;
        default:
            console.log("Error: Invalid component type selected.");
            return;
    }
    pluginApi("setLayerMetadata", { name: "exportType", value: newExportType });
}

function onRenderTextChanged() {
    "use strict";
    pluginApi("setLayerMetadata", { name: "textIsRendered", value: this.checked });
}

function onQmlTypeChanged() {
    "use strict";
    pluginApi("setLayerMetadata", { name: "typeName", value: this.value });
}

function onImportStatementsChanged(e) {
    "use strict";
    let imports = this.value.split(/\r?\n/);
    pluginApi("setLayerMetadata", { name: "extraImports", value: imports });
}

function onQmlPropertiesChanged(e) {
    "use strict";
    let properties = this.value.split(/\r?\n/);
    pluginApi("setLayerMetadata", { name: "qmlProperties", value: properties });
}

function onAliasChanged(e) {
    "use strict";
    pluginApi("setLayerMetadata", { name: "createAlias", value: this.checked });
}

function onClipChanged(e) {
    "use strict";
    pluginApi("setLayerMetadata", { name: "clip", value: this.checked });
}

function onVisibleChanged(e) {
    "use strict";
    pluginApi("setLayerMetadata", { name: "qmlVisible", value: this.checked });
}

function onAnnotationChanged(e) {
    "use strict";
    pluginApi("setLayerMetadata", { name: "annotation", value: this.value });
}

function onExportPath() {
    "use strict";
    pluginApi("setUserExportPath");
}

function onExportPathReset() {
    "use strict";
    pluginApi("resetDocumentMetadata", "jsonExportPath");
}

function onDeleteScaleFormat() {
    "use strict";
    this.parentNode.parentNode.remove();
    updateExportOptions();
}

function onScaleFormatChanged() {
    let field = this.parentNode.parentNode.parentNode;
    let assetLabel = field.getElementsByTagName("input")[0];
    let scale, format;
    for (let combo of field.getElementsByTagName("select")) {
        if (combo.id === "format_combo") {
            format = combo.value;
        } else if (combo.id === "scale_combo") {
            scale = combo.value;
        }
    }
    assetLabel.value = `assetname${scale == 2 ? "@2" : ""}.${format}`;
    updateExportOptions();
}

function onExport() {
    $(IdDict.ShowExportDir).addClass("is-occupied-hidden");
    $(IdDict.ProgressOK).addClass("is-occupied-hidden");
    $(IdDict.ProgressLoading).addClass("is-loading");
    $(IdDict.ProgressLog).text("");
    $(IdDict.ProgressModal).addClass("is-active");
    pluginApi("exportActiveDocument");
}

function onCloseExport() {
    $(IdDict.ProgressModal).removeClass("is-active");
}

function onShowExportDir() {
    pluginApi("showExportDir");
}

function onOrganizeInPages(e) {
    const organize = $(IdDict.OrganizeInPages).prop("checked");
    pluginApi("setOrganizeInPages", organize);
}

function onExportOptionChanged() {
    let userOptions = {};
    userOptions.saveMetadata = $(IdDict.PngMetadata).prop("checked");
    userOptions.progressiveJpg = $(IdDict.JpgProgressive).prop("checked");
    userOptions.compression = 1.0;
    userOptions.includeNamespaces = $(IdDict.SvgNamespaces).prop("checked");
    userOptions.compactSvg = $(IdDict.SvgCompact).prop("checked");
    pluginApi("setFormatOptions", userOptions);
}

function onUiScaleChanged(e) {
    pluginApi("setUiScale", this.value);
}

function onHome() {
    showPanel(this.id);
}

function onSettings() {
    showPanel(this.id);
    pluginApi("updateDocumentDetails");
}

function onAbout() {
    "use strict";
    showPanel(this.id);
}

function onLicense() {
    pluginApi("showLicense");
}

function onHelp() {
    "use strict";
    pluginApi("showHelp");
}

//*****************************************************************************
// API's exposed to Sketch plugin
//*****************************************************************************
window.onLayerSelectionChange = function (selection) {
    if (selection.length === 1) {
        updateControlsState(selection[0], 1);
        setInvalidExportAsVisible(false);
        updateLayerData(selection[0]);
        return;
    } else if (selection.length === 0) {
        setInvalidExportAsVisible(false);
        updateControlsState({}, 0);
        updateLayerData({});
        return;
    }
    setInvalidExportAsVisible(true);

    // Handle Multi Selection
    let data = {};
    let names = [];
    data.layerType = selection[0].layerType;
    data.exportType = selection[0].exportType;
    selection.forEach(layer => {
        names.push(layer.layerName);
        if (data.hasOwnProperty("layerType") && data.layerType !== layer.layerType) {
            delete data.layerType;
        }
        if (data.hasOwnProperty("exportType") && data.exportType !== layer.exportType) {
            delete data.exportType;
        }
        data.parentIsSkipped = data.parentIsSkipped | layer.parentIsSkipped;
    });

    data.layerName = names.join(",");

    updateControlsState(data, selection.length);
    updateLayerData(data);
};

window.onDocDetailsChanged = function (details) {
    $(IdDict.PathEdit).val(details.jsonExportPath);
    $(IdDict.UiScaleEdit).val(details.uiScaleFactor)
    $(IdDict.DocName).val(details.name);
    $(IdDict.FormatPanel).empty();
    $(IdDict.OrganizeInPages).prop("checked", details.organizeInPages);

    if (details.hasOwnProperty("formatOptions")) {
        let pngOptions = details.formatOptions[VEF.Png];
        $(IdDict.PngMetadata).prop("checked", pngOptions["save-for-web"]);

        let jpgOptions = details.formatOptions[VEF.Jpg];
        $(IdDict.JpgProgressive).prop("checked", jpgOptions.progressive);

        let svgOptions = details.formatOptions[VEF.Svg];
        $(IdDict.SvgNamespaces).prop("checked", svgOptions["include-namespaces"]);
        $(IdDict.SvgCompact).prop("checked", svgOptions.compact);
    }

    // The first entry is added with add button.
    addScaleFormat(details.scaleFormat[0].formats, details.scaleFormat[0].scales, true, true);
    delete details.scaleFormat[0];
    details.scaleFormat.forEach(item => {
        addScaleFormat(item.formats, item.scales, false, true);
    });
};

window.updateTheme = function (theme) {
    $(`link[href="./../css/${theme}.css"]`).remove();
    $("head").append(`<link rel="stylesheet" href="./../css/${theme}.css" type="text/css" />`);
};

window.onExportProgress = function (value) {
    $(IdDict.ProgressBar).attr("value", value);
};

window.onExportLog = function (logLevel, str) {
    let prefix = "";
    switch (logLevel) {
        case Constants.LogLevels.Error:
            prefix = "Error: "
            break;
        case Constants.LogLevels.Warning:
            prefix = "Warning: "
            break;
    }
    $(IdDict.ProgressLog).append(`${prefix}${str}\n`);
};

window.onDebugLog = function (logLevel, str) {
    switch (logLevel) {
        case Constants.LogLevels.Info:
            console.info(str);
            break;
        case Constants.LogLevels.Error:
            console.error(str);
            break;
        case Constants.LogLevels.Warning:
            console.warn(str);
            break;
        default:
            console.log(str);
    }
};

window.onExportFinished = function (success) {
    $(IdDict.ProgressLoading).removeClass("is-loading");
    $(IdDict.ProgressOK).removeClass("is-occupied-hidden");
    $(IdDict.ShowExportDir).removeClass("is-occupied-hidden");
};

window.onVersionDetails = function (versionStr, buildStr) {
    $(IdDict.Version).text(versionStr);
    $(IdDict.Build).text(buildStr);
};

//*****************************************************************************
// Helper Api's
//*****************************************************************************
function capitalize(str) {
    if (!str) return "";
    return str[0].toUpperCase() + str.slice(1);
}

function strippedId(idStr) {
    if (idStr.constructor !== String || idStr.length < 2) {
        return "unknown";
    }
    return idStr.slice(1);
}

function pluginApi(api, value) {
    if (!api) {
        console.error(`Invalid plugin API call ${api}`);
        return;
    }
    if (value !== undefined) {
        window.postMessage(api, JSON.stringify(value));
    } else {
        window.postMessage(api);
    }
}

function disableControls(controlsVal) {
    $(IdDict.QmlId).attr("disabled", controlsVal & Controls.QmlId ? true : false);
    $(IdDict.ExportAs).attr("disabled", controlsVal & Controls.ExportAs ? true : false);
    $(IdDict.RenderText).attr("disabled", controlsVal & Controls.RenderText ? true : false);
    $(IdDict.QmlType).attr("disabled", controlsVal & Controls.QmlType ? true : false);
    $(IdDict.Imports).attr("disabled", controlsVal & Controls.Imports ? true : false);
    $(IdDict.QmlProps).attr("disabled", controlsVal & Controls.QmlProps ? true : false);
    $(IdDict.Alias).attr("disabled", controlsVal & Controls.Alias ? true : false);
    $(IdDict.Clip).attr("disabled", controlsVal & Controls.Clip ? true : false);
    $(IdDict.Visible).attr("disabled", controlsVal & Controls.Visible ? true : false);
    $(IdDict.Annotation).attr("disabled", controlsVal & Controls.Annotation ? true : false);
}

function updateControlsState(d, selectedLayersCount = -1) {
    let cValue = Controls.None;

    // Enable all
    disableControls(Controls.None);
    $(IdDict.SkippedWarning).addClass("is-occupied-hidden");

    if (selectedLayersCount == 0 || d.parentIsSkipped) {
        if (d.parentIsSkipped) {
            $(IdDict.SkippedWarning).removeClass("is-occupied-hidden");
        }
        // Disable all metadata controls when no layer selection
        disableControls(Controls.LayerMeta);
        return;
    } else if (selectedLayersCount > 1) {
        cValue |= Controls.QmlId;
    }
    if (d.hasOwnProperty("exportType")) {
        let et = d.exportType;
        if (et == ET.MergedToParent || et == ET.SkipExport) {
            cValue |=
                Controls.QmlType |
                Controls.Imports |
                Controls.QmlProps |
                Controls.Alias |
                Controls.Clip |
                Controls.Visible;
        }
    }
    let textIsRendered = d.hasOwnProperty("textIsRendered") && d.textIsRendered;
    if (d.hasOwnProperty("layerType")) {
        let lt = d.layerType;
        if (lt !== SLT.Text) {
            cValue |= Controls.RenderText;
        }

        if (lt === SLT.Hotspot || lt === SLT.SymbolInstance) {
            // Do not allow changing QML type of Hotspots and Symbols instance layers
            cValue |= Controls.QmlType;
        }
        // Export as options
        // Enable all export-as options
        $(IdDict.ExportAs).prop("disabled", false);
        let disableChild = lt === SLT.Artboard || lt === SLT.SymbolMaster;
        let disableComponent = lt !== SLT.Artboard && lt !== SLT.SymbolMaster;
        let disableMerged =
            lt === SLT.Artboard ||
            lt === SLT.Hotspot ||
            lt === SLT.SymbolMaster ||
            lt === SLT.SymbolInstance ||
            (lt === SLT.Text && !textIsRendered);
        $(`${IdDict.ExportAs} option[value=${ET.AsChild}]`).attr("disabled", disableChild);
        $(`${IdDict.ExportAs} option[value=${ET.AsComponent}]`).attr("disabled", disableComponent);
        $(`${IdDict.ExportAs} option[value=${ET.MergedToParent}]`).attr("disabled", disableMerged);
    } else {
        $(`${IdDict.ExportAs} option[value=${ET.AsComponent}]`).attr("disabled", true);
        $(`${IdDict.ExportAs} option[value=${ET.MergedToParent}]`).attr("disabled", true);
    }
    disableControls(cValue);
}

function setInvalidExportAsVisible(visible) {
    const invalidValue = Constants.ExportInvalidType;
    let have = $(`${IdDict.ExportAs} option[value=${invalidValue}]`).length !== 0;
    if (visible && !have) {
        $(IdDict.ExportAs).append(`<option value=${invalidValue}>No Change</option>`);
    } else if (!visible && have) {
        $(`${IdDict.ExportAs} option[value=${invalidValue}]`).remove();
    }
}

function updateLayerData(d) {
    $(IdDict.DocName).val(d.documentName); // See QDS-545
    $(IdDict.LayerName).val(d.hasOwnProperty("layerName") ? d.layerName : "...");

    $(IdDict.QmlId).val(d.hasOwnProperty("qmlId") ? d.qmlId : "...");

    if (d.hasOwnProperty("exportType")) {
        $(IdDict.ExportAs).val(d.exportType);
    } else {
        $(IdDict.ExportAs).val(Constants.ExportInvalidType);
    }

    $(IdDict.RenderText).prop("checked", d.hasOwnProperty("textIsRendered") && d.textIsRendered);
    $(IdDict.QmlType).val(d.hasOwnProperty("typeName") ? d.typeName : "");

    $(IdDict.Imports).val(d.hasOwnProperty("extraImports") ? d.extraImports.join("\n") : "");

    $(IdDict.QmlProps).val(d.hasOwnProperty("qmlProperties") ? d.qmlProperties.join("\n") : "");

    $(IdDict.Annotation).val(d.hasOwnProperty("annotation") ? d.annotation : "");

    $(IdDict.Alias).prop("checked", d.hasOwnProperty("createAlias") && d.createAlias);
    $(IdDict.Clip).prop("checked", d.hasOwnProperty("clip") && d.clip);
    $(IdDict.Visible).prop("checked", d.hasOwnProperty("qmlVisible") && d.qmlVisible);
}

function updateExportOptions() {
    let uniqueOptions = new Map();
    let scaleFormat = [];
    let c = $(`${IdDict.FormatPanel} select`);
    for (let index = 0; index < c.length; ++index) {
        let format = c[index].value;
        let scale = c[++index].value;
        let key = `${format}::${scale}`;
        if (!uniqueOptions.has(key)) {
            uniqueOptions.set(key, [format, scale]);
            let entry = Object.assign({}, { formats: format, scales: scale }, CEO);
            scaleFormat.push(entry);
        }
    }
    pluginApi("setDocumentMetadata", { name: "scaleFormat", value: scaleFormat });
}

function populateScaleFormatCombo(formatCombo, scaleCombo, f = VEF.Png, s = VES.Normal) {
    for (let format in Constants.ValidExportFormats) {
        let value = Constants.ValidExportFormats[format];
        let isSelected = value == f ? " selected" : "";
        formatCombo.append(`<option value=${value}${isSelected}>${value.toUpperCase()}</option>`);
    }
    for (let scale in Constants.ValidExportScale) {
        let value = Constants.ValidExportScale[scale];
        let isSelected = value == s ? " selected" : "";
        scaleCombo.append(`<option value=${value}${isSelected}>${value}X</option>`);
    }
}

function addScaleFormat(formatValue, scaleValue, isDefault, noUpdate) {
    "use strict";
    if (!formatValue) {
        formatValue = Constants.ValidExportFormats.Png;
    }
    if (!scaleValue) {
        scaleValue = Constants.ValidExportScale.Normal;
    }

    let container = $(IdDict.FormatPanel);
    if (container[0].children.length >= 4) {
        return;
    }
    let formatCombo = $("<select/>").attr({ id: strippedId(IdDict.FormatCombo) });
    let scaleCombo = $("<select/>").attr({ id: strippedId(IdDict.FormatScale) });
    let buttonId = isDefault ? strippedId(IdDict.FormatAdd) : strippedId(IdDict.FormatDelete);
    let buttonIcon = isDefault ? "plus" : "delete";
    populateScaleFormatCombo(formatCombo, scaleCombo, formatValue, scaleValue);

    let field = $(`<div class="field has-addons">
    <div class="control is-expanded"><input class="input is-small has-text-centered" value="assetname.png" type="text" readonly></div>
    <div class="control"><div class="select is-fullwidth is-small">
    ${formatCombo.prop("outerHTML")}</div></div>
    <div class="control"><div class="select is-fullwidth is-small">
    ${scaleCombo.prop("outerHTML")}</div></div>
    <p class="control">
        <a id=${buttonId} class="button is-primary is-small"><span class="icon"><img src="./../icons/svg/${buttonIcon}.svg"></span></a>
    </p>
    </div>`);
    container.append(field);
    if (!noUpdate) {
        updateExportOptions();
    }
}

function showPanel(crumbId) {
    let panels = new Map();
    panels.set(IdDict.Home, null);
    panels.set(IdDict.Settings, IdDict.PanelSettings);
    panels.set(IdDict.About, IdDict.PanelAbout);
    let expandPanel = (panel, show) => {
        panel[0].style.height = `${Constants.PanelHeight}px`;
        panel[0].style.maxHeight = show ? `${Constants.PanelHeight}px` : "0px";
        panel[0].style.opacity = show ? 1 : 0;
    };
    panels.forEach((p, c) => {
        if (crumbId === strippedId(c)) {
            $(c).addClass("is-active");
            if (p) {
                expandPanel($(p), true);
            }
        } else {
            $(c).removeClass("is-active");
            if (p) {
                expandPanel($(p), false);
            }
        }
    });
}
